import { challengeGameFor, isOpenChallengeDate, utcDateKey } from "./dailyChallenge";
import { ARCADE_GAME_KINDS, isArcadeGameKind, type ArcadeGameKind } from "./games";

// Leaderboard rules shared by the site and convex/arcadeScores.ts (Convex
// bundles this file, so it only imports relative files with no browser code).
// Everything a posted score has to pass lives here: the board keys, the
// nickname rules, the rate limit and the plausibility caps.

// Points a very good player can earn per second of play, per game. Measured
// with the demo bot (a near perfect player) across chill, normal and hype, a
// 124 and a 172 BPM song and silence, on phone and desktop sizes, over seven
// minute runs. The worst average rate seen is rounded up here.
// src/tests/arcade-leaderboard.test.ts replays real bot runs against these.
export const POINTS_PER_SECOND: Record<ArcadeGameKind, number> = {
  "beat-lanes": 4000,
  "night-drive": 1050,
  "bass-blaster": 7200,
  "pulse-pads": 3900,
  "vine-runner": 450,
  "vinyl-orbit": 1150,
};

// Headroom on top of the measured rate: twice as much, growing with run length
// because every game scores faster as the levels climb.
export const CAP_MARGIN = 2;
export const CAP_GROWTH_SECONDS = 600;
export const CAP_FLOOR = 2_000;

export const MIN_DURATION_SEC = 3;
export const MAX_DURATION_SEC = 4 * 60 * 60;

export function scoreCap(game: ArcadeGameKind, durationSec: number): number {
  const seconds = Math.max(0, Math.min(MAX_DURATION_SEC, durationSec));
  return Math.ceil(CAP_FLOOR + CAP_MARGIN * POINTS_PER_SECOND[game] * seconds * (1 + seconds / CAP_GROWTH_SECONDS));
}

// Levels come every 30 seconds in most games and faster in Night Drive's
// sectors; this is a loose ceiling that no honest run gets near.
export const levelCap = (durationSec: number): number => 3 + Math.ceil(Math.max(0, durationSec) / 12);

export const SUBMIT_LIMIT = 20;
export const SUBMIT_WINDOW_MS = 60 * 60 * 1000;

export function pruneSubmitStamps(stamps: readonly number[], now: number): number[] {
  const cutoff = now - SUBMIT_WINDOW_MS;
  return stamps.filter((stamp) => Number.isFinite(stamp) && stamp > cutoff && stamp <= now).sort((a, b) => a - b).slice(-SUBMIT_LIMIT);
}

export function consumeSubmit(stamps: readonly number[], now: number): { allowed: boolean; stamps: number[] } {
  const live = pruneSubmitStamps(stamps, now);
  if (live.length >= SUBMIT_LIMIT) return { allowed: false, stamps: live };
  return { allowed: true, stamps: [...live, now] };
}

// Boards. A song and game has an all time board and one per UTC day; the daily
// challenge has its own board per day, keyed by the song too so a score posted
// against the wrong song never lands on the real one.
export const ALL_TIME = "all";
export const dayPeriod = (date: string) => `d:${date}`;
export const DAY_PERIOD_PREFIX = "d:";
export const boardKey = (songSlug: string, game: ArcadeGameKind) => `${songSlug}/${game}`;
export const challengeBoardKey = (date: string, songSlug: string, game: ArcadeGameKind) => `challenge/${date}/${songSlug}/${game}`;

export type BoardPeriod = "today" | "all";

export const BOARD_SIZE = 10;
export const RANK_SCAN_CAP = 1000;

export const SONG_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,78}[a-z0-9])?$/;
export const isArcadeSongSlug = (value: unknown): value is string => typeof value === "string" && SONG_SLUG_PATTERN.test(value);

// Nicknames: 2 to 16 characters of letters, numbers, spaces, dots, dashes and
// underscores. Accents are folded (José becomes Jose) and a short blocklist
// keeps slurs and impersonation off the board.
export const NICKNAME_MIN = 2;
export const NICKNAME_MAX = 16;

// Parts are caught anywhere in the name; words only on their own, so Hancock,
// Dickens and Spicy stay allowed.
const BLOCKED_PARTS = ["fuck", "shit", "cunt", "nigg", "fagg", "bitch", "whore", "slut", "rapist", "nazi", "hitler", "kkk", "porn", "pussy", "penis", "vagina", "retard", "twat", "kike", "dildo", "jizz"];
const BLOCKED_WORDS = ["ass", "arse", "cum", "sex", "fag", "rape", "tits", "dick", "cock", "boob", "boobs", "wank", "spic", "chink", "anus", "piss"];
const RESERVED_PARTS = ["truevined", "admin", "moderator", "official", "support", "staff"];

const LEET: Record<string, string> = { "0": "o", "1": "i", "3": "e", "4": "a", "5": "s", "7": "t", "8": "b", "@": "a", $: "s", "!": "i" };

function foldForCheck(value: string): string {
  return value
    .toLowerCase()
    .split("")
    .map((char) => LEET[char] ?? char)
    .join("");
}

export type NicknameResult = { ok: true; nickname: string } | { ok: false; reason: string };

export function cleanNickname(raw: unknown): NicknameResult {
  if (typeof raw !== "string") return { ok: false, reason: `Pick a name, ${NICKNAME_MIN} to ${NICKNAME_MAX} characters.` };
  const folded = raw.normalize("NFKD").replace(/[̀-ͯ]/g, "");
  const kept = folded.replace(/[^A-Za-z0-9 ._-]/g, "").replace(/\s+/g, " ").trim();
  if (kept.length < NICKNAME_MIN || kept.length > NICKNAME_MAX) return { ok: false, reason: `Pick a name, ${NICKNAME_MIN} to ${NICKNAME_MAX} characters.` };
  if (!/[A-Za-z0-9]/.test(kept)) return { ok: false, reason: "Use at least one letter or number." };
  const squashed = foldForCheck(kept).replace(/[^a-z]/g, "");
  const words = foldForCheck(kept).split(/[^a-z]+/).filter(Boolean);
  if (BLOCKED_PARTS.some((part) => squashed.includes(part)) || words.some((word) => BLOCKED_WORDS.includes(word))) {
    return { ok: false, reason: "That name is not allowed. Try another." };
  }
  if (RESERVED_PARTS.some((part) => squashed.includes(part))) return { ok: false, reason: "That name is taken. Try another." };
  return { ok: true, nickname: kept };
}

export interface ScoreSubmission {
  songSlug: string;
  game: string;
  score: number;
  level: number;
  durationSec: number;
  nickname: string;
  challenge?: string | null;
}

export interface CheckedSubmission {
  songSlug: string;
  game: ArcadeGameKind;
  score: number;
  level: number;
  durationSec: number;
  nickname: string;
  today: string;
  // The challenge day this score also counts for, or null.
  challengeDate: string | null;
}

export type SubmissionCheck = { ok: true; value: CheckedSubmission } | { ok: false; reason: string };

export const IMPLAUSIBLE_SCORE_MESSAGE = "That score does not add up for the time played, so it was not posted.";

export function checkSubmission(input: ScoreSubmission, nowMs: number): SubmissionCheck {
  if (!isArcadeGameKind(input.game)) return { ok: false, reason: "Unknown game." };
  const game = input.game;
  const songSlug = String(input.songSlug || "").toLowerCase();
  if (!isArcadeSongSlug(songSlug)) return { ok: false, reason: "Unknown song." };
  const nickname = cleanNickname(input.nickname);
  if (nickname.ok === false) return nickname;
  const { score, level, durationSec } = input;
  if (!Number.isInteger(score) || score < 1) return { ok: false, reason: "Only a run that scored can be posted." };
  if (!Number.isFinite(durationSec) || durationSec < MIN_DURATION_SEC || durationSec > MAX_DURATION_SEC) return { ok: false, reason: "That run is too short to post." };
  if (!Number.isInteger(level) || level < 1 || level > levelCap(durationSec)) return { ok: false, reason: IMPLAUSIBLE_SCORE_MESSAGE };
  if (score > scoreCap(game, durationSec)) return { ok: false, reason: IMPLAUSIBLE_SCORE_MESSAGE };
  const challenge = typeof input.challenge === "string" ? input.challenge : null;
  const challengeDate = challenge && isOpenChallengeDate(challenge, nowMs) && challengeGameFor(challenge) === game ? challenge : null;
  return {
    ok: true,
    value: { songSlug, game, score, level, durationSec: Math.round(durationSec), nickname: nickname.nickname, today: utcDateKey(nowMs), challengeDate },
  };
}

// The rows one accepted score may raise: the all time and today boards for
// its song and game, plus the challenge board when it counts for one.
export function boardsFor(value: CheckedSubmission): Array<{ boardKey: string; period: string }> {
  const key = boardKey(value.songSlug, value.game);
  const boards = [
    { boardKey: key, period: ALL_TIME },
    { boardKey: key, period: dayPeriod(value.today) },
  ];
  if (value.challengeDate) boards.push({ boardKey: challengeBoardKey(value.challengeDate, value.songSlug, value.game), period: dayPeriod(value.challengeDate) });
  return boards;
}

export const periodFor = (period: BoardPeriod, nowMs: number) => (period === "all" ? ALL_TIME : dayPeriod(utcDateKey(nowMs)));

// How long the day boards are kept before the nightly prune removes them.
export const DAY_BOARD_RETENTION_DAYS = 14;

export const KNOWN_GAMES: readonly ArcadeGameKind[] = ARCADE_GAME_KINDS;

// Review hold. The caps above are twice the best rate the demo bot (a near
// perfect player) ever averaged, so an honest run sits at or under about half
// the cap. A score above REVIEW_RATIO of the cap is not refused, it waits in
// the admin review queue and reaches the boards only once approved.
export const REVIEW_RATIO = 0.6;

export const needsReview = (game: ArcadeGameKind, score: number, durationSec: number): boolean => score > REVIEW_RATIO * scoreCap(game, durationSec);

export const REVIEW_MESSAGE = "Big score! It goes on the board as soon as it has been checked.";
export const BANNED_MESSAGE = "Scores from this account are not accepted.";
export const NICKNAME_TAKEN_MESSAGE = "That name is taken. Try another.";

// Two players may not post under names that read the same: case, spacing,
// punctuation and look-alike digits are ignored, so "T0bi" cannot pose as
// "Tobi" at the top of a board.
export function nicknameKey(nickname: string): string {
  return foldForCheck(nickname).replace(/[^a-z0-9]/g, "");
}

// A placeholder name for a player whose name was reset by the admin.
export const resetNicknameFor = (playerKey: string): string => `Player ${playerKey.replace(/^p:/, "").slice(0, 4).toUpperCase()}`;

// High scores (the hall of fame on /games). Built on the server from the all
// time rows every few minutes, never from a client: one line per player per
// game (their best on any song), and the Legends table, which gives each
// player points for where they finish on each game's list, the way a racing
// championship does. The endless run is its own list and scores no points.
export const HIGH_SCORE_LIST_SIZE = 10;
export const LEGENDS_SIZE = 20;
export const LEGEND_POINTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1] as const;

export interface HighScoreRow {
  playerKey: string;
  nickname: string;
  score: number;
  level: number;
  songSlug: string;
  game: string;
  updatedAt: number;
}

export interface HighScoreEntry {
  rank: number;
  nickname: string;
  score: number;
  level: number;
  songSlug: string;
}

export interface LegendEntry {
  rank: number;
  nickname: string;
  points: number;
  wins: number;
  games: number;
}

export interface HighScores {
  games: Record<ArcadeGameKind, HighScoreEntry[]>;
  endless: HighScoreEntry[];
  legends: LegendEntry[];
  players: number;
}

function rankRows<T extends { value: number }>(rows: T[]): Array<T & { rank: number }> {
  let rank = 0;
  let previous: number | null = null;
  return rows.map((row, index) => {
    if (row.value !== previous) rank = index + 1;
    previous = row.value;
    return { ...row, rank };
  });
}

// The endless run posts under this song slug (ENDLESS_SLUG in lineup.ts,
// which Convex cannot bundle; the tests keep the two equal).
export const ENDLESS_BOARD_SLUG = "endless";

export function buildHighScores(rows: readonly HighScoreRow[], endlessSlug = ENDLESS_BOARD_SLUG): HighScores {
  // Each player shows under the name they used last.
  const names = new Map<string, { nickname: string; at: number }>();
  for (const row of rows) {
    const known = names.get(row.playerKey);
    if (!known || row.updatedAt > known.at) names.set(row.playerKey, { nickname: row.nickname, at: row.updatedAt });
  }
  const nameOf = (playerKey: string) => names.get(playerKey)?.nickname ?? "Player";

  const bestList = (candidates: readonly HighScoreRow[]): HighScoreEntry[] => {
    const best = new Map<string, HighScoreRow>();
    for (const row of candidates) {
      const current = best.get(row.playerKey);
      // Higher score wins; on a tie the earlier run keeps the spot.
      if (!current || row.score > current.score || (row.score === current.score && row.updatedAt < current.updatedAt)) best.set(row.playerKey, row);
    }
    const sorted = [...best.values()].sort((a, b) => b.score - a.score || a.updatedAt - b.updatedAt).slice(0, HIGH_SCORE_LIST_SIZE);
    return rankRows(sorted.map((row) => ({ value: row.score, row }))).map(({ rank, row }) => ({ rank, nickname: nameOf(row.playerKey), score: row.score, level: row.level, songSlug: row.songSlug }));
  };

  const games = {} as Record<ArcadeGameKind, HighScoreEntry[]>;
  const tally = new Map<string, { points: number; wins: number; games: number }>();
  for (const game of ARCADE_GAME_KINDS) {
    const candidates = rows.filter((row) => row.game === game && row.songSlug !== endlessSlug);
    games[game] = bestList(candidates);
    // Points go by finishing position, so a tie shares the higher points.
    const playersOfGame = new Map<string, number>();
    for (const row of candidates) playersOfGame.set(row.playerKey, Math.max(playersOfGame.get(row.playerKey) ?? 0, row.score));
    const ordered = rankRows([...playersOfGame.entries()].sort((a, b) => b[1] - a[1]).map(([playerKey, value]) => ({ playerKey, value })));
    for (const { playerKey, rank } of ordered) {
      const entry = tally.get(playerKey) ?? { points: 0, wins: 0, games: 0 };
      entry.games += 1;
      entry.points += LEGEND_POINTS[rank - 1] ?? 0;
      if (rank === 1) entry.wins += 1;
      tally.set(playerKey, entry);
    }
  }

  const legendRows = [...tally.entries()]
    .filter(([, entry]) => entry.points > 0)
    .sort((a, b) => b[1].points - a[1].points || b[1].wins - a[1].wins || b[1].games - a[1].games || nameOf(a[0]).localeCompare(nameOf(b[0])))
    .slice(0, LEGENDS_SIZE);
  const legends = rankRows(legendRows.map(([playerKey, entry]) => ({ value: entry.points, playerKey, entry }))).map(({ rank, playerKey, entry }) => ({
    rank,
    nickname: nameOf(playerKey),
    points: entry.points,
    wins: entry.wins,
    games: entry.games,
  }));

  return { games, endless: bestList(rows.filter((row) => row.songSlug === endlessSlug)), legends, players: names.size };
}
